import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { useAccount } from "../../app/session";
import { date } from "../../lib/format";
import { t } from "../../lib/sprache";
import { Icon } from "../../components/Icon";
import { PillView } from "../../components/ui";
import { Fehler, Laden } from "./parts";
import { Uebersicht, testendeText, testphasenListe, type Testphase } from "./Uebersicht";
import { ANFRAGEN_KEY, anfrageStatus, type DemoAnfrage } from "./DemoAnfragen";
import { TrafficDashboard } from "./Traffic";
import { Basiszins } from "./Basiszins";
import { gruss } from "./traffic-model";
import { konsoleGruppen, istBetrieb } from "./konsole-nav";
import { useKonsolenRolle } from "./rolle";
import { DemoKarte, useDemoStatus } from "./DemoKonto";
import { useAction } from "../../components/ui";
import { OwnerKuerzel, StufePill, useIch, useStaff, useStart } from "./crm-teile";
import { PIPELINE, NEBEN, setOwner, stufeLabel, taskDone } from "./crm-data";
import { HeuteKachel } from "./Kalender";

/**
 * Startseite je Rolle (Vertrieb-Konsole, Inhaber 02.11.2026). Admin: wie bisher die Übersicht (Kacheln, „Braucht
 * Aufmerksamkeit“, Traffic). Vertrieb: was heute zu tun ist (Tests kurz vor dem Ende, offene Demo-Anfragen; ab 0460 auch fällige
 * Aufgaben, neue Leads ohne Zuständigen und Signale „aktiv im Test“).
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;

export function KonsoleStart() {
  // 0530: Kachel „Heute“ aus dem Team-Kalender je Rolle (Vertrieb: eigene Calls und Aufgaben; Admin und Entwicklung: das Team)
  return istBetrieb(useKonsolenRolle()) ? <Uebersicht heute={<HeuteKachel />} vertrieb={<Vertriebsstand />} /> : <VertriebStart oben={<><HeuteKachel /><DemoSchnell /><VertriebHeute /></>} />;
}

/** 0461: Demo-Konto oben auf dem Start des Vertriebs (öffnen und zurücksetzen) */
function DemoSchnell() {
  const q = useDemoStatus();
  if (!q.data || q.data.length === 0) return null;
  return <div className="grid tk-crm-kundenakte">{q.data.map((d) => <DemoKarte key={d.org_id} d={d} kompakt />)}</div>;
}

export function Gruss({ titel }: { titel?: string }) {
  const { account } = useAccount();
  const vorname = account?.platformName?.trim().split(/\s+/)[0] ?? account?.memberships.map((m) => m.display_name?.trim().split(/\s+/)[0]).find(Boolean) ?? null;
  const jetzt = new Date();
  return (
    <>
      <title>{`${titel ?? "Start"} · Konsole · CreatorOffice`}</title>
      <div className="tk-konsole-gruss"><h1>{gruss(jetzt.getHours(), t)}{vorname ? `, ${vorname}` : ""}</h1>
        <small>{jetzt.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</small></div>
    </>
  );
}

/** Tests, die in den nächsten drei Tagen enden (auch heute und gestern abgelaufen, vor dem nächtlichen Lauf) */
export function testsBaldZuEnde(konten: Testphase[], heute: Date, tage = 3) {
  return testphasenListe(konten, heute).filter((k) => k.tage >= -1 && k.tage <= tage);
}

export function VertriebStart({ oben }: { oben?: ReactNode }) {
  return (
    <>
      <Gruss />
      {oben}
      <div className="grid two tk-start-raster">
        <TestsBaldZuEnde />
        <OffeneDemoAnfragen />
      </div>
    </>
  );
}

function TestsBaldZuEnde() {
  const q = useQuery({ queryKey: ["platform_trials", false], refetchInterval: 5 * 60_000,
    queryFn: async () => parse<Testphase[]>(await api.read("platform_trials", { p_internal: false })) });
  const liste = q.data ? testsBaldZuEnde(q.data, new Date()) : [];
  return (
    <section className="panel stack" aria-labelledby="start-tests">
      <h2 id="start-tests">Tests, die in 3 Tagen enden</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !q.data ? <Laden /> : liste.length === 0
        ? <p className="muted">Kein Test endet in den nächsten drei Tagen.</p>
        : (
          <ul className="tk-betrieb-liste">
            {liste.map((k) => (
              <li key={k.id} className="tk-start-zeile">
                <span><Link to={`/intern/agenturen/${k.id}`}>{k.name}</Link>{k.tarif ? <small> · {k.tarif}</small> : null}<br />
                  <small>{date(k.ende)} · {testendeText(k.tage, t)}</small></span>
                {k.zahlt ? <span className="pill success">zahlt automatisch</span> : <span className="pill warning">läuft aus</span>}
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}

function OffeneDemoAnfragen() {
  const q = useQuery({ queryKey: ANFRAGEN_KEY, queryFn: async () => parse<DemoAnfrage[]>(await api.read("platform_demo_requests", {})) ?? [] });
  const offen = (q.data ?? []).filter((a) => a.status === "open" || a.status === "slots_sent");
  return (
    <section className="panel stack" aria-labelledby="start-demo">
      <h2 id="start-demo">Offene Demo-Anfragen</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !q.data ? <Laden /> : offen.length === 0
        ? <p className="muted">Keine offene Demo-Anfrage.</p>
        : (
          <ul className="tk-betrieb-liste">
            {offen.slice(0, 6).map((a) => (
              <li key={a.id} className="tk-start-zeile">
                <span><strong>{a.company}</strong><br /><small>{a.name} · {date(a.created_at)}</small></span>
                <PillView pill={anfrageStatus(a)} />
              </li>
            ))}
          </ul>
        )}
      <p><Link to="/intern/demo-termine">Zu den Demo-Anfragen und Terminen</Link></p>
    </section>
  );
}

/** Website-Traffic als eigene Seite unter Marketing (vorher nur oben in der Übersicht, dort bleibt er) */
export function TrafficSeite() {
  const [tage, setTage] = useState(30);
  const [intern, setIntern] = useState(false);
  return (
    <>
      <title>Website-Traffic · Konsole · CreatorOffice</title>
      <h1>Website-Traffic</h1>
      <div className="row tk-betrieb-filter" style={{ gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label>Zeitraum{" "}
          <select value={tage} onChange={(e) => setTage(Number(e.target.value))}>{[7, 30, 90].map((x) => <option key={x} value={x}>{x} Tage</option>)}</select>
        </label>
        <label className="row" style={{ gap: 6, alignItems: "center" }}><input type="checkbox" checked={intern} onChange={(e) => setIntern(e.target.checked)} /> Intern zeigen</label>
      </div>
      <TrafficDashboard tage={tage} intern={intern} />
    </>
  );
}

/** Basiszins als eigene Seite unter Technik (steht weiter auch bei den Produkt-Standards) */
export function BasiszinsSeite() {
  return (
    <>
      <title>Basiszins · Konsole · CreatorOffice</title>
      <h1>Basiszins</h1>
      <Basiszins />
    </>
  );
}

/** Am Handy: alle Bereiche der Seitenleiste nach Gruppen (wie „Mehr“ im Agenturportal) */
export function KonsoleMehr() {
  const rolle = useKonsolenRolle();
  return (
    <>
      <title>Mehr · Konsole · CreatorOffice</title>
      <h1>Alle Bereiche</h1>
      {konsoleGruppen(rolle).map((g) => (
        <nav key={g.gruppe} className="panel stack tk-menu" aria-label={g.gruppe}>
          <h2 className="tk-menu-titel">{g.gruppe}</h2>
          {g.bereiche.map((b) => (
            <Link key={b.to} to={b.to} className="task">
              <span className="row"><Icon name={b.icon} />{b.label}</span>
              <span aria-hidden="true">›</span>
            </Link>
          ))}
        </nav>
      ))}
    </>
  );
}

/**
 * Vertrieb heute (0460): fällige Aufgaben und nächste Schritte (eigene und ohne Zuständigen), neue Leads ohne Zuständigen mit
 * „Mir zuweisen“ und die Signale „aktiv im Test“ der letzten Zeit.
 */
export function VertriebHeute() {
  const q = useStart();
  const ich = useIch();
  const staff = useStaff();
  const { busy, run } = useAction();
  if (q.error) return <Fehler error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Laden />;
  const d = q.data;
  const faellig = d.faellig.length + d.naechste_schritte.length;
  return (
    <div className="grid three tk-start-raster">
      <section className="panel stack" aria-labelledby="start-faellig">
        <h2 id="start-faellig">Heute fällig · {faellig}</h2>
        {faellig === 0 ? <p className="muted">Nichts fällig. Neue Aufgaben entstehen bei Kündigungswunsch, Testende oder von Hand.</p> : (
          <ul className="tk-betrieb-liste">
            {d.faellig.map((a) => (
              <li key={a.id} className="tk-start-zeile">
                <span><Link to={`/intern/leads/${a.lead_id}`}>{a.company ?? "Lead"}</Link><br /><small>{a.body}{a.due_at && a.due_at < d.heute ? ` · seit ${date(a.due_at)}` : ""}</small></span>
                <button type="button" disabled={!!busy} aria-busy={busy === `t-${a.id}`} onClick={() => void run(`t-${a.id}`, () => taskDone(api, a.id), "Aufgabe erledigt.")}>Erledigt</button>
              </li>
            ))}
            {d.naechste_schritte.map((n) => (
              <li key={`n-${n.lead_id}`} className="tk-start-zeile">
                <span><Link to={`/intern/leads/${n.lead_id}`}>{n.company ?? "Lead"}</Link><br /><small>Nächster Schritt: {n.next_step ?? "–"} · {date(n.next_step_at)}</small></span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel stack" aria-labelledby="start-ohne">
        <h2 id="start-ohne">Neue Leads ohne Zuständigen · {d.ohne_zustaendigen.length}</h2>
        {d.ohne_zustaendigen.length === 0 ? <p className="muted">Alle Leads haben einen Zuständigen.</p> : (
          <ul className="tk-betrieb-liste">
            {d.ohne_zustaendigen.slice(0, 8).map((l) => (
              <li key={l.id} className="tk-start-zeile">
                <span><Link to={`/intern/leads/${l.id}`}>{l.company ?? "Lead"}</Link><br /><small>{l.stage_label} · {date(l.created_at)}</small></span>
                {ich && <button type="button" disabled={!!busy} aria-busy={busy === `o-${l.id}`} onClick={() => void run(`o-${l.id}`, () => setOwner(api, l.id, ich), "Dir zugewiesen.")}>Mir zuweisen</button>}
              </li>
            ))}
          </ul>
        )}
        <p><Link to="/intern/leads">Alle Leads</Link></p>
      </section>
      <section className="panel stack" aria-labelledby="start-aktiv">
        <h2 id="start-aktiv">Signale: aktiv im Test · {d.aktiv_im_test.length}</h2>
        {d.aktiv_im_test.length === 0 ? <p className="muted">Gerade kein Testkonto mit Aktivität. Signale: Creator angelegt, Vertrag versendet, Rechnung erstellt, Instagram verbunden.</p> : (
          <ul className="tk-betrieb-liste">
            {d.aktiv_im_test.slice(0, 8).map((l) => (
              <li key={l.id} className="tk-start-zeile">
                <span><Link to={l.org_id ? `/intern/agenturen/${l.org_id}` : `/intern/leads/${l.id}`}>{l.company ?? "Lead"}</Link><br />
                  <small>aktiv seit {date(l.since)}{l.trial_ends_at ? ` · Test bis ${date(l.trial_ends_at)}` : ""}</small></span>
                <StufePill stufe="aktiv_im_test" />
              </li>
            ))}
          </ul>
        )}
        <p className="muted"><small>{d.meine} offene {d.meine === 1 ? "Lead ist" : "Leads sind"} dir zugeordnet.</small> <OwnerKuerzel staff={staff.data} id={ich} /></p>
      </section>
    </div>
  );
}

/** Vertriebsstand für den Admin: Leads je Stufe, Rabatt-Vorschläge des Vertriebs, fällige Aufgaben */
export function Vertriebsstand() {
  const q = useStart();
  const { busy, run } = useAction();
  if (q.error) return <Fehler error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Laden />;
  const d = q.data;
  return (
    <section className="stack" aria-labelledby="start-vertrieb">
      <h2 id="start-vertrieb" className="tk-abschnitt">Vertriebsstand</h2>
      <div className="grid two">
        <section className="panel stack" aria-labelledby="start-stufen">
          <h2 id="start-stufen">Leads je Stufe</h2>
          <ul className="tk-crm-stufen">
            {[...PIPELINE, ...NEBEN].map((s) => <li key={s}><span>{stufeLabel(s)}</span><strong className="num">{d.stufen[s] ?? 0}</strong></li>)}
          </ul>
          <p><Link to="/intern/pipeline">Zur Pipeline</Link> · {d.faellig.length} fällige Aufgaben · {d.ohne_zustaendigen.length} ohne Zuständigen</p>
        </section>
        <section className="panel stack" aria-labelledby="start-rabatt">
          <h2 id="start-rabatt">Rabatt-Vorschläge · {d.fuer_admin.length}</h2>
          {d.fuer_admin.length === 0 ? <p className="muted">Kein offener Vorschlag des Vertriebs.</p> : (
            <ul className="tk-betrieb-liste">
              {d.fuer_admin.map((a) => (
                <li key={a.id} className="tk-start-zeile">
                  <span><Link to={`/intern/leads/${a.lead_id}`}>{a.body}</Link><br /><small>von {a.von ?? "–"} · {date(a.created_at)}</small></span>
                  <button type="button" disabled={!!busy} aria-busy={busy === `r-${a.id}`} onClick={() => void run(`r-${a.id}`, () => taskDone(api, a.id), "Vorschlag erledigt.")}>Erledigt</button>
                </li>
              ))}
            </ul>
          )}
          <p><Link to="/intern/rabattcodes">Rabattcode anlegen</Link></p>
        </section>
      </div>
    </section>
  );
}
